// react
import { useState } from 'react';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useNavigate } from '@tanstack/react-router';

// material ui
import { Box, Typography } from '@mui/material';
import { DataGridPremium } from '@mui/x-data-grid-premium';

// api
import {
  type GetAlarmConfigurationDto,
  type Severity,
  useDeleteAlarmConfigurationMutation,
} from '@/api/generated/recalc/endpoints/v2/alarmConfiguration';

// shared components
import { DataGridToolbar } from '@/components/ui/datagrid/DataGridToolbar';
import RowActions from '@/components/ui/datagrid/RowActions';

// feature-components
import { SeverityBars } from '@/features/alarm-hub/components/SeverityBars';

// components
import { useDatagridLocaleText } from '@/hooks/useDatagridLocaleText';
import { showSnackbar } from '@/redux/snackbarSlice';

// relative
import DeleteAlarmConfigurationDialog from './DeleteAlarmConfigurationDialog';

type AlarmConfigurationRow = GetAlarmConfigurationDto;

const AlarmConfigurationDataGrid = ({
  alarmConfigurations,
  accountId,
}: {
  alarmConfigurations: AlarmConfigurationRow[];
  accountId: number;
}) => {
  const { t } = useTranslation('alarms');
  const localeText = useDatagridLocaleText();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [selectedConfigToDelete, setSelectedConfigToDelete] =
    useState<AlarmConfigurationRow | null>(null);

  const rowsWithId = alarmConfigurations.filter(
    (row) => row.alarmConfigurationId != null,
  );

  const [
    deleteAlarmConfiguration,
    { isLoading: isDeletingAlarmConfiguration },
  ] = useDeleteAlarmConfigurationMutation();

  const handleEditAlarmConfiguration = (config: AlarmConfigurationRow) => {
    if (!config.alarmConfigurationId || !accountId) {
      return;
    }

    navigate({
      to: `/$accountId/alarms/configuration/${config.alarmConfigurationId}`,
      params: {
        accountId: String(accountId),
        alarmConfigurationId: String(config.alarmConfigurationId),
      } as never,
    });
  };

  const handleDeleteAlarmConfiguration = (config) => {
    setSelectedConfigToDelete(config);
  };

  const handleCloseDeleteDialog = () => {
    if (isDeletingAlarmConfiguration) {
      return;
    }
    setSelectedConfigToDelete(null);
  };

  const handleConfirmDeleteAlarmConfiguration = async () => {
    if (!selectedConfigToDelete?.alarmConfigurationId) {
      return;
    }

    const result = await deleteAlarmConfiguration({
      id: selectedConfigToDelete.alarmConfigurationId,
    });

    if (!('error' in result)) {
      dispatch(
        showSnackbar({
          message: t('messages.alarm_configuration_deleted', {
            defaultValue: 'Alarm configuration deleted successfully',
          }),
          severity: 'success',
        }),
      );

      setSelectedConfigToDelete(null);
    }
    // Errors are handled globally by apiErrorMiddleware (snackbar + App Insights).
  };

  return (
    <>
      <DataGridPremium
        rows={rowsWithId}
        getRowId={(row) => row.alarmConfigurationId as number}
        columns={[
          {
            field: 'title',
            headerName: t('columns.title', { defaultValue: 'Title' }),
            flex: 1,
          },
          {
            field: 'severity',
            headerName: t('columns.severity', { defaultValue: 'Severity' }),
            flex: 1,
            renderCell: ({ row }) => {
              const severity = row.severity;
              const severityLabel =
                typeof severity === 'string'
                  ? t(`severity.${severity.toLowerCase()}`, {
                      defaultValue: severity,
                    })
                  : undefined;

              return severity ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    height: '100%',
                  }}
                >
                  <SeverityBars
                    severity={severity as Severity}
                    label={severityLabel}
                  />
                </Box>
              ) : null;
            },
          },
          {
            field: 'created',
            headerName: t('columns.created_at', { defaultValue: 'Created' }),
            type: 'dateTime',
            flex: 1,
            valueGetter: (_value, row) =>
              row.created ? new Date(row.created) : null,
            renderCell: ({ row }) => {
              const createdDate = row.created ? new Date(row.created) : null;
              return (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    height: '100%',
                  }}
                >
                  <Typography variant="body2">
                    {createdDate?.toLocaleString() ?? ''}
                  </Typography>
                </Box>
              );
            },
          },
          {
            field: 'alarmType',
            headerName: t('columns.type', { defaultValue: 'Type' }),
            flex: 1,
            valueGetter: (_value, row) =>
              row.alarmType
                ? t(`alarm_types.${row.alarmType}`, {
                    defaultValue: row.alarmType,
                  })
                : '',
          },
          {
            field: 'alarmSubtype',
            headerName: t('columns.subtype', { defaultValue: 'Sub Type' }),
            flex: 1,
          },
          // {
          //   field: 'message',
          //   headerName: 'Message',
          //   flex: 1,
          // },
          {
            field: 'associations',
            headerName: t('columns.associations', {
              defaultValue: 'Associations',
            }),
            flex: 1,
            renderCell: ({ row }) => {
              const associations = row.associations;
              return associations ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    height: '100%',
                    gap: 0.5,
                  }}
                >
                  <Typography variant="body2">
                    {associations?.['units']
                      ?.map((unit) => String(unit.name))
                      .join(', ')}
                  </Typography>
                  <Typography variant="body2">
                    (
                    {associations?.['dataStreams']
                      ?.map((s) => String(s.dataType))
                      .join(', ')}
                    )
                  </Typography>
                </Box>
              ) : null;
            },
          },
          {
            field: 'actions',
            headerName: t('columns.actions', { defaultValue: 'Actions' }),
            renderCell: ({ row }) => (
              <RowActions
                onEdit={() => handleEditAlarmConfiguration(row)}
                onDelete={() => handleDeleteAlarmConfiguration(row)}
              />
            ),
          },
        ]}
        showToolbar
        localeText={localeText}
        slots={{
          toolbar: DataGridToolbar,
        }}
        hideFooter
        autoHeight
      />

      <DeleteAlarmConfigurationDialog
        open={Boolean(selectedConfigToDelete)}
        configurationToDelete={selectedConfigToDelete}
        onCancel={handleCloseDeleteDialog}
        onConfirm={handleConfirmDeleteAlarmConfiguration}
      />
    </>
  );
};

export default AlarmConfigurationDataGrid;
